/**
 * OrgTree — recursive org-chart renderer with dual layout modes.
 *
 *   • `node.layout === "horizontal"` (default) — children fan out
 *     left-to-right beneath the parent (classic tree).
 *   • `node.layout === "vertical"` — children stack top-to-bottom in
 *     a single column beneath the parent (used for team columns in
 *     SS2-style expo/event org charts).
 *
 * Each parent picks its own layout independently. Layouts nest — a
 * "vertical" parent whose child itself has "horizontal" grandchildren
 * still renders the grandchildren horizontally beneath that child.
 *
 * Recursion uses React.createElement to keep the JSX literal depth
 * shallow so the visual-edits Babel plugin doesn't blow its stack.
 */
import { Fragment, createElement, useState } from "react";
import { Plus, ArrowRight, ArrowLeft, ArrowUp, Info, Phone, Mail, ChevronUp, ChevronDown } from "lucide-react";
import "./OrgTree.css";

// Tailwind palette per approved color key.
export const ORG_PALETTE = {
  black: {
    chip: "bg-zinc-900",
    card: "border-zinc-700 bg-zinc-900",
    accent: "bg-zinc-600",
    header: "bg-zinc-900 text-white",
  },
  slate: {
    chip: "bg-slate-500",
    card: "border-slate-400 bg-slate-50 text-slate-900",
    accent: "bg-slate-500",
    header: "bg-slate-700 text-white",
  },
  yellow: {
    chip: "bg-yellow-400",
    card: "border-yellow-400 bg-yellow-50 text-yellow-950",
    accent: "bg-yellow-400",
    header: "bg-yellow-400 text-yellow-950",
  },
  green: {
    chip: "bg-emerald-500",
    card: "border-emerald-400 bg-emerald-50 text-emerald-950",
    accent: "bg-emerald-500",
    header: "bg-emerald-600 text-white",
  },
  orange: {
    chip: "bg-orange-500",
    card: "border-orange-400 bg-orange-50 text-orange-950",
    accent: "bg-orange-500",
    header: "bg-orange-500 text-white",
  },
  red: {
    chip: "bg-rose-500",
    card: "border-rose-400 bg-rose-50 text-rose-950",
    accent: "bg-rose-500",
    header: "bg-rose-600 text-white",
  },
  blue: {
    chip: "bg-sky-500",
    card: "border-sky-400 bg-sky-50 text-sky-950",
    accent: "bg-sky-500",
    header: "bg-sky-600 text-white",
  },
  violet: {
    chip: "bg-violet-500",
    card: "border-violet-400 bg-violet-50 text-violet-950",
    accent: "bg-violet-500",
    header: "bg-violet-600 text-white",
  },
  pink: {
    chip: "bg-pink-500",
    card: "border-pink-400 bg-pink-50 text-pink-950",
    accent: "bg-pink-500",
    header: "bg-pink-500 text-white",
  },
};

const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toLocaleUpperCase("tr");

function NodeCard({ node, onSelect, onAddChild, onAddSibling, onAddParent, isSelected, readOnly, userMap, dnd, onNodeDrop, isMatch, collapsed, descCount, onToggleCollapse, multiSelected, isLinkSource }) {
  const palette = ORG_PALETTE[node.color] || ORG_PALETTE.slate;
  const isDept = node.kind === "department";
  const size = node.size || "md";
  const dark = node.color === "black";
  const sizeCls =
    size === "lg"
      ? "min-w-[250px] max-w-[330px]"
      : size === "sm"
        ? "min-w-[140px] max-w-[190px]"
        : "min-w-[190px] max-w-[240px]";
  const linkedUser = node.user_id ? userMap[node.user_id] : null;
  const displayName = linkedUser?.name || node.person_name || null;
  const displayPhone = linkedUser?.phone || node.phone || null;
  const displayEmail = linkedUser?.email || node.email || null;
  const displayPhoto = linkedUser?.photo_url || null;
  const hasAvatar = !isDept && !!(displayPhoto || displayName);
  const hasDetails = !isDept && !!(node.tasks || displayPhone || displayEmail || displayPhoto);
  const [taskOpen, setTaskOpen] = useState(false);
  const [dropHover, setDropHover] = useState(false);
  return (
    <div
      data-testid={`org-chart-node-${node.id}`}
      className={[
        "group node-card relative inline-block cursor-pointer rounded-xl border-2 shadow-sm transition-all",
        sizeCls,
        isDept
          ? `${palette.header} border-black/10 px-4 ${size === "lg" ? "py-3" : "py-2"}`
          : `${palette.card} ${dark ? "" : "bg-white/80 backdrop-blur-sm"} px-3 py-2`,
        hasAvatar ? "mt-4" : "",
        isSelected
          ? "ring-2 ring-offset-2 ring-violet-500 scale-[1.02]"
          : dropHover
            ? "ring-2 ring-offset-2 ring-emerald-500 scale-[1.04]"
            : isLinkSource
              ? "ring-2 ring-offset-2 ring-sky-500 scale-[1.03]"
              : multiSelected
                ? "ring-2 ring-offset-2 ring-blue-500"
                : isMatch
                  ? "ring-2 ring-offset-2 ring-amber-400"
                  : "hover:shadow-md hover:-translate-y-0.5",
      ].join(" ")}
      onClick={(e) => onSelect && onSelect(node.id, e)}
      draggable={!!dnd}
      onDragStart={
        dnd
          ? (e) => {
              e.dataTransfer.setData("text/org-node", node.id);
              e.dataTransfer.effectAllowed = "move";
            }
          : undefined
      }
      onDragOver={
        dnd
          ? (e) => {
              if (Array.from(e.dataTransfer.types).includes("text/org-node")) {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDropHover(true);
              }
            }
          : undefined
      }
      onDragLeave={dnd ? () => setDropHover(false) : undefined}
      onDrop={
        dnd
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              setDropHover(false);
              const src = e.dataTransfer.getData("text/org-node");
              if (src && src !== node.id && onNodeDrop) onNodeDrop(src, node.id);
            }
          : undefined
      }
    >
      {!isDept && (
        <div
          className={[
            "absolute left-0 top-2 bottom-2 w-1 rounded-r-full",
            palette.accent,
          ].join(" ")}
          aria-hidden
        />
      )}
      {/* Avatar — linked user's photo, or colored initials fallback. */}
      {hasAvatar &&
        (displayPhoto ? (
          <img
            src={displayPhoto}
            alt=""
            data-testid={`org-chart-node-avatar-${node.id}`}
            className="absolute -top-4 left-1/2 h-8 w-8 -translate-x-1/2 rounded-full object-cover shadow ring-2 ring-white"
            draggable={false}
          />
        ) : (
          <span
            data-testid={`org-chart-node-avatar-${node.id}`}
            className={[
              "absolute -top-4 left-1/2 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full text-[11px] font-bold text-white shadow ring-2 ring-white",
              palette.chip,
            ].join(" ")}
          >
            {initialsOf(displayName)}
          </span>
        ))}
      {/* Info button lives in the top-right absolute so title/name can
          center in the full card width without any offset. */}
      {hasDetails && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            setTaskOpen((v) => !v);
          }}
          onMouseEnter={() => setTaskOpen(true)}
          onMouseLeave={() => setTaskOpen(false)}
          data-testid={`org-chart-node-info-${node.id}`}
          title="Detay"
          className="absolute right-1.5 top-1.5 flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full bg-zinc-800/10 text-zinc-700 hover:bg-zinc-800/20"
        >
          <Info className="h-3 w-3" strokeWidth={2.25} />
          {taskOpen && (
            <div
              data-testid={`org-chart-node-tooltip-${node.id}`}
              role="tooltip"
              className="pointer-events-none absolute left-full top-0 z-30 ml-1 w-60 whitespace-normal break-words rounded-md bg-zinc-900 px-2.5 py-2 text-left text-[11px] leading-snug text-white shadow-lg"
            >
              {displayPhoto && (
                <img
                  src={displayPhoto}
                  alt=""
                  className="mb-2 h-16 w-16 rounded-lg object-cover ring-1 ring-white/20"
                />
              )}
              {displayName && (
                <div className="mb-1 text-[12px] font-semibold text-white">
                  {displayName}
                </div>
              )}
              {displayPhone && (
                <div className="mb-1 flex items-center gap-1 text-[10.5px] text-zinc-300">
                  <Phone className="h-2.5 w-2.5" strokeWidth={2} />
                  <span>{displayPhone}</span>
                </div>
              )}
              {displayEmail && (
                <div className="mb-1 flex items-center gap-1 text-[10.5px] text-zinc-300">
                  <Mail className="h-2.5 w-2.5" strokeWidth={2} />
                  <span className="break-all">{displayEmail}</span>
                </div>
              )}
              {node.tasks && (
                <>
                  <div className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-zinc-400">
                    Görev / Sorumluluk
                  </div>
                  <div className="whitespace-pre-wrap break-words text-[11px] text-zinc-100">
                    {node.tasks}
                  </div>
                </>
              )}
            </div>
          )}
        </span>
      )}
      {isDept ? (
        <div
          className={`px-1 text-center font-extrabold uppercase tracking-wider ${
            size === "lg" ? "text-[16px]" : size === "sm" ? "text-[11px]" : "text-[13px]"
          }`}
          data-testid={`org-chart-node-title-${node.id}`}
        >
          {node.title || "DEPARTMAN"}
        </div>
      ) : (
      <div className={`flex flex-col items-center px-3 py-1 text-center ${hasAvatar ? "pt-3.5" : ""} ${dark ? "text-white" : ""}`}>
        {node.title ? (
          <>
            <div
              className={`w-full font-semibold leading-tight uppercase tracking-wide ${
                size === "lg" ? "text-[15px]" : size === "sm" ? "text-[11px]" : "text-[13px]"
              }`}
              data-testid={`org-chart-node-title-${node.id}`}
            >
              {node.title}
            </div>
            {displayName && (
              <div className={`mt-0.5 w-full truncate ${size === "sm" ? "text-[10px]" : size === "lg" ? "text-[12px]" : "text-[11px]"} ${dark ? "text-zinc-300" : "text-zinc-700"}`}>
                {displayName}
              </div>
            )}
          </>
        ) : displayName ? (
          <div
            className={`w-full font-semibold leading-tight tracking-wide ${
              size === "lg" ? "text-[15px]" : size === "sm" ? "text-[11px]" : "text-[13px]"
            } ${dark ? "text-white" : "text-zinc-900"}`}
            data-testid={`org-chart-node-title-${node.id}`}
          >
            {displayName}
          </div>
        ) : (
          <div
            className={`w-full text-[13px] font-medium leading-tight italic ${dark ? "text-zinc-500" : "text-zinc-400"}`}
            data-testid={`org-chart-node-title-${node.id}`}
          >
            Adsız
          </div>
        )}
      </div>
      )}
      {/* Collapse / expand toggle — bottom-left, only when node has children. */}
      {descCount > 0 && onToggleCollapse && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse(node.id);
          }}
          data-testid={`org-chart-collapse-${node.id}`}
          title={collapsed ? `Dalı aç (${descCount} düğüm)` : "Dalı daralt"}
          className={
            collapsed
              ? "absolute -bottom-3 left-3 z-10 flex h-6 min-w-[24px] items-center justify-center gap-0.5 rounded-full bg-violet-600 px-1.5 text-[10px] font-bold text-white shadow-sm transition-colors hover:bg-violet-700"
              : "absolute -bottom-3 left-3 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 group-hover:flex"
          }
        >
          {collapsed ? (
            <>
              {descCount}
              <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
            </>
          ) : (
            <ChevronUp className="h-3.5 w-3.5" strokeWidth={2.25} />
          )}
        </button>
      )}
      {!readOnly && onAddChild && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddChild(node.id);
          }}
          data-testid={`org-chart-add-child-${node.id}`}
          title={node.layout === "vertical" ? "Altına düğüm ekle (dikey)" : "Altına düğüm ekle (yatay)"}
          className="absolute -bottom-3 left-1/2 hidden h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 group-hover:flex"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      )}
      {!readOnly && onAddSibling && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddSibling(node.id, "before");
            }}
            data-testid={`org-chart-add-sibling-left-${node.id}`}
            title="Soluna düğüm ekle"
            className="absolute -left-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 group-hover:flex"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddSibling(node.id, "after");
            }}
            data-testid={`org-chart-add-sibling-${node.id}`}
            title="Sağına düğüm ekle"
            className="absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 group-hover:flex"
          >
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
        </>
      )}
      {!readOnly && onAddParent && !node.parent_id && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddParent(node.id);
          }}
          data-testid={`org-chart-add-parent-${node.id}`}
          title="Üstüne kök düğüm ekle"
          className={[
            "absolute hidden h-6 w-6 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 group-hover:flex",
            hasAvatar ? "-top-3 right-6" : "-top-3 left-1/2 -translate-x-1/2",
          ].join(" ")}
        >
          <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      )}
    </div>
  );
}

/**
 * Render a subtree — the node's own card followed by its children
 * block (either a horizontal <ul> or a vertical column stack). Returns
 * a React Fragment (multiple siblings) that the caller wraps in an
 * <li> (for horizontal parents) or <div class="v-item"> (for vertical
 * parents).
 */
function countDesc(node) {
  return (node.children || []).reduce((a, c) => a + 1 + countDesc(c), 0);
}

/** Aşağı doğru her seviyede en fazla 1 görünür kişi çocuğu var mı? */
function isPureChain(n, opts) {
  const kids = n.children || [];
  const collapsed = opts.collapsedIds ? opts.collapsedIds.has(n.id) : false;
  if (collapsed || kids.length === 0) return true;
  if (kids.length > 1) return false;
  if (kids[0].kind === "department") return false;
  return isPureChain(kids[0], opts);
}

function renderNode(node, opts) {
  const kids = node.children || [];
  const collapsed = opts.collapsedIds ? opts.collapsedIds.has(node.id) : false;
  const card = createElement(NodeCard, {
    node,
    onSelect: opts.onSelect,
    onAddChild: opts.onAddChild,
    onAddSibling: opts.onAddSibling,
    onAddParent: opts.onAddParent,
    isSelected: opts.selectedId === node.id,
    readOnly: opts.readOnly,
    userMap: opts.userMap || {},
    dnd: !!opts.dnd && !opts.readOnly,
    onNodeDrop: opts.onNodeDrop,
    isMatch: opts.highlightIds ? opts.highlightIds.has(node.id) : false,
    multiSelected: opts.multiIds ? opts.multiIds.has(node.id) : false,
    isLinkSource: opts.linkSourceId === node.id,
    collapsed,
    descCount: countDesc(node),
    onToggleCollapse: opts.onToggleCollapse,
  });

  if (kids.length === 0 || collapsed) return card;

  const isVertical = node.layout === "vertical";
  const soloKid = kids.length === 1 ? kids[0] : null;
  // SS2-style chain: a person with exactly ONE person child renders the
  // child directly beneath, connected by a right-side bracket instead of
  // the classic center drop line.
  const isChain =
    !isVertical &&
    soloKid &&
    node.kind !== "department" &&
    soloKid.kind !== "department" &&
    isPureChain(soloKid, opts);
  if (isChain) {
    const lu = soloKid.user_id ? (opts.userMap || {})[soloKid.user_id] : null;
    const hasAv = !!(lu?.photo_url || lu?.name || soloKid.person_name);
    return createElement(
      "div",
      { className: "chain" },
      card,
      createElement(
        "div",
        {
          className: hasAv ? "chain-item chain-avatar" : "chain-item",
          key: soloKid.id,
        },
        renderNode(soloKid, opts),
      ),
    );
  }

  const childrenBlock = isVertical
    ? createElement(
        "div",
        { className: "v-list", key: "vlist" },
        kids.map((c) => {
          const lu = c.user_id ? (opts.userMap || {})[c.user_id] : null;
          const hasAv =
            c.kind !== "department" &&
            !!(lu?.photo_url || lu?.name || c.person_name);
          return createElement(
            "div",
            { className: hasAv ? "v-item v-avatar" : "v-item", key: c.id },
            renderNode(c, opts),
          );
        }),
      )
    : createElement(
        "ul",
        { key: "ul" },
        kids.map((c) =>
          createElement("li", { key: c.id }, renderNode(c, opts)),
        ),
      );

  return createElement(Fragment, null, card, childrenBlock);
}

/**
 * Public API — renders the whole forest of root nodes.
 */
export function OrgTree({
  roots,
  onSelect,
  onAddChild,
  onAddSibling,
  onAddParent,
  selectedId,
  readOnly,
  userMap,
  dnd,
  onNodeDrop,
  highlightIds,
  collapsedIds,
  onToggleCollapse,
  multiIds,
  linkSourceId,
}) {
  const opts = {
    onSelect,
    onAddChild,
    onAddSibling,
    onAddParent,
    selectedId,
    readOnly,
    userMap,
    dnd,
    onNodeDrop,
    highlightIds,
    collapsedIds,
    onToggleCollapse,
    multiIds,
    linkSourceId,
  };
  return (
    <div className="serpix-orgchart flex justify-center">
      {createElement(
        "ul",
        null,
        (roots || []).map((n) =>
          createElement("li", { key: n.id }, renderNode(n, opts)),
        ),
      )}
    </div>
  );
}
